import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDuration } from "../../utils";
import { type CompareSide, compareSideColor, compareSideFill } from "./processCompareModel";
import type { ComparisonGroup } from "./processComparison";
import { STEP_H, STEP_W, roundedPath } from "./processGraphLayout";
import { useElkLayout } from "./useElkLayout";
import { edgeKey, graphWithInstances } from "./processGraphModel";

export default function ProcessComparisonMap({
  group,
  side,
}: { group: ComparisonGroup; side: CompareSide }) {
  const { t } = useTranslation();
  const markerId = `compare-arrow-${useId()}`;
  const graph = useMemo(() => graphWithInstances(group.process.nodes, group.runs), [group]);
  const { layout, failed } = useElkLayout(graph);
  // Each group is its own map: never draw a previous group's layout while this one computes.
  const result = layout?.sourceGraph === graph ? layout : null;
  const [zoom, setZoom] = useState(0.8);
  const viewport = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = viewport.current;
    if (!element || !result) return;
    const fit = () =>
      setZoom(
        Math.min(
          1,
          (element.clientWidth - 24) / result.layout.width,
          (element.clientHeight - 24) / result.layout.height,
        ),
      );
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    return () => observer.disconnect();
  }, [result]);
  return (
    <div className="overflow-hidden rounded-xl border border-outline bg-surface">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-outline px-3 py-2">
        <span className="text-xs text-text-dim">
          {t(group.runs.length === 1 ? "processPage.singleMap" : "processPage.aggregateMap", {
            count: group.runs.length,
          })}
        </span>
      </div>
      <p className="px-3 py-2 text-xs text-text-dim">
        {t(group.runs.length === 1 ? "processPage.mapValues" : "processPage.groupMapValues")}
      </p>
      <div ref={viewport} className="h-[21rem] overflow-hidden bg-canvas p-3">
        {!result && (
          <output className="p-4 text-sm text-text-mid">
            {t(failed ? "processPage.mapFailed" : "generating_graph")}
          </output>
        )}
        {result && (
          <svg
            aria-label={`${t("process_graph_label")}: ${group.label}`}
            width={result.layout.width * zoom}
            height={result.layout.height * zoom}
            viewBox={`0 0 ${result.layout.width} ${result.layout.height}`}
            className="mx-auto block"
          >
            <title>{`${t("process_graph_label")}: ${group.label}`}</title>
            <defs>
              <marker
                id={markerId}
                markerWidth="8"
                markerHeight="8"
                refX="7"
                refY="4"
                orient="auto"
              >
                <path d="M 0 0 L 8 4 L 0 8 z" fill={compareSideColor(side)} />
              </marker>
            </defs>
            {result.edges.map(edge => {
              const points = result.layout.edgePoints.get(edgeKey(edge.from, edge.to));
              return (
                points && (
                  <path
                    key={edgeKey(edge.from, edge.to)}
                    d={roundedPath(points)}
                    fill="none"
                    stroke={compareSideColor(side)}
                    strokeWidth={1.5}
                    opacity={0.85}
                    markerEnd={`url(#${markerId})`}
                  />
                )
              );
            })}
            {result.nodes.map(node => {
              const point = result.layout.nodePos.get(node.id);
              if (!point) return null;
              if (node.id === graph.start || node.id === graph.end)
                return (
                  <g key={node.id}>
                    <circle
                      cx={point.x}
                      cy={point.y}
                      r={8}
                      fill="var(--color-surface)"
                      stroke="var(--color-text-dim)"
                    />
                    <text
                      x={point.x}
                      y={point.y + (node.id === graph.start ? -14 : 24)}
                      textAnchor="middle"
                      fontSize={11}
                      fill="var(--color-text-dim)"
                    >
                      {t(node.id === graph.start ? "start" : "end")}
                    </text>
                  </g>
                );
              const seconds =
                node.instances.reduce((sum, occurrence) => sum + occurrence.duration, 0) /
                Math.max(1, group.runs.length);
              return (
                <foreignObject
                  key={node.id}
                  x={point.x - STEP_W / 2}
                  y={point.y - STEP_H / 2}
                  width={STEP_W}
                  height={STEP_H}
                >
                  <div
                    className="flex h-full w-full flex-col justify-center rounded-lg border px-2 text-center"
                    style={{
                      borderColor: compareSideColor(side),
                      backgroundColor: compareSideFill(side),
                    }}
                  >
                    <span
                      className="block w-full truncate text-xs font-medium text-text"
                      title={node.name}
                    >
                      {node.name}
                    </span>
                    <span className="text-[10px] text-text-dim">
                      {node.app ? `${node.app} · ` : ""}
                      {node.instances.length / Math.max(1, group.runs.length) < 0.1
                        ? "<0.1"
                        : Number(
                            (node.instances.length / Math.max(1, group.runs.length)).toFixed(1),
                          )}
                      × · {formatDuration(seconds)}
                    </span>
                  </div>
                </foreignObject>
              );
            })}
          </svg>
        )}
      </div>
    </div>
  );
}
